import { HttpClient } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ConfigurationTerrain, TerrainFileValidator, TerrainTypeModel } from 'src/app/models/terrains';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { EndpointsService } from 'src/app/services/endpoints.service';
import { ModalConfirmationService } from 'src/app/services/modal-confirmation.service';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { ProgressService } from 'src/app/services/progress.service';
import { ProjectService } from 'src/app/services/project.service';
import { UiBlockService } from 'src/app/services/ui-block.service';
import { UtilsService } from 'src/app/services/utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ImportFormat } from 'src/app/models/importFormat';

@Component({
  selector: 'popup-configuration-terrain',
  templateUrl: './configuration-terrain.component.html',
  styleUrls: ['../../app.component.scss'],
  standalone: false
})
export class PopupConfigurationTerrainComponent {


  configurationTerrainForm: FormGroup;
  activeChanged: boolean = false; //TODO
  @Input() selectedRow: number = -1;

  selectedType: TerrainTypeModel;
  zipFiles: File;

  originalName: string;

  terrain: ConfigurationTerrain

  @Input() previousBsModalRef: BsModalRef

  readonly importFormat: ImportFormat = ImportFormat.ZIP_FORMAT;

  constructor(
    public configurationController: ConfigurationService,
    public bsModalRef: BsModalRef,
    private modalService: ModalControlService,
    private uiBlock: UiBlockService,
    private projectService: ProjectService,
    private utilsService: UtilsService,
    private confirmationModalService: ModalConfirmationService,
    private progressService: ProgressService
  ) { }

  ngOnInit() {
    let fileFormControl: FormControl;
    if (this.configurationController.terrainEditMode) {
      this.originalName = this.configurationController.terrainToEdit.name;
      this.selectedType = this.configurationController.terrainTypes.find(terrainType => terrainType.id == this.configurationController.terrainToEdit.terrainTypeId);
      this.terrain = this.configurationController.terrainToEdit;
      fileFormControl = new FormControl(this.zipFiles);
    } else {
      this.terrain = new ConfigurationTerrain();
      this.selectedType = this.configurationController.terrainTypes[0];
      fileFormControl = new FormControl(this.zipFiles, [Validators.required, this.fileValidator]);
    }

    this.configurationTerrainForm = new FormGroup({
      name: new FormControl(this.terrain.name, [Validators.required]),
      terrainType: new FormControl(this.selectedType, [Validators.required]),
      active: new FormControl(this.terrain.active, [Validators.required]),
      zipFile: fileFormControl
    });

    if (this.terrain.name && (this.terrain.name == ConfigurationTerrain.DEFAULT_TERRAIN_NAME || this.terrain.name == ConfigurationTerrain.DEFAULT_GTOPO30)) {
      this.configurationTerrainForm.get("name").disable();
      this.configurationTerrainForm.get("terrainType").disable();
      this.configurationTerrainForm.get("zipFile").disable();
    }
  }

  fileValidator(control: FormControl) {
    if (control == null) {
      return {};
    }
    return null;
  }

  async zipFilesUpload(event) {
    this.zipFiles = <File>event.target.files[0];
    this.uiBlock.startBlock("Validating Terrain File...");
    const isValid = await TerrainFileValidator.validateTerrainFile(this.zipFiles, this.selectedType.type);
    if (!isValid) {
      this.uiBlock.stopBlock();
      this.changeTerrainType();
      this.modalService.showMesssageModal(
        'Terrain File Error',
        `Invalid ${this.selectedType.type} directory. There aren't any ${this.selectedType.type} files here`
      );
    } else {
      let resolution = TerrainFileValidator.DEFAULT_RESOLUTION;
      const resolutionFile: FormData = await TerrainFileValidator.getTerrainResolutionFile(this.zipFiles, this.selectedType.type);
      try {
        if (resolutionFile != null) {
          resolutionFile.append("projectId", this.projectService.activeProject.id.toString());
          resolution = await this.configurationController.getTerrainResolution(resolutionFile);
        }
      } finally {
        this.uiBlock.stopBlock();

        this.confirmationModalService.showMesssageConfirmationModal("Terrain Resolution",
          "The selected terrain resolution is " + resolution + " arc-seconds (approx. " + this.utilsService.arcSecondsToMeters(resolution) + " meters). " +
          "Do you confirm this is the correct resolution?", "Yes", "No",
          () => { },
          () => {
            this.changeTerrainType();
          });
      }
    }


  }

  changeTerrainType() {
    if (this.configurationController.terrainEditMode) {
      if (this.selectedType.id != this.terrain.terrainTypeId) {
        this.configurationTerrainForm.get("zipFile").setValidators([Validators.required, this.fileValidator]);
      } else {
        this.configurationTerrainForm.get("zipFile").clearValidators();
      }
    }
    this.configurationTerrainForm.get("zipFile").reset();
    this.zipFiles = null;
    (<HTMLInputElement>document.getElementById("zipFile")).value = null;
    this.configurationTerrainForm.get("zipFile").updateValueAndValidity();
  }

  close(): void {
    this.bsModalRef.hide();
  }

  closePreviousModal(): void {
    this.previousBsModalRef.hide();
  }

  async save(): Promise<void> {
    if (!this.utilsService.validateFileName(this.terrain.name)) {
      this.modalService.showMesssageModal(
        'Terrain Error',
        'Terrain name is invalid. Please choose a diferent name.'
      );
      return;
    }

    if (this.configurationController.terrainEditMode) {
      this.updateTerrain();
    } else {
      this.saveTerrain();
    }

  }

  private async updateTerrain() {
    for (const terrain of this.configurationController.savedTerrains$.getValue()) {
      if (terrain.name == this.terrain.name && terrain.id != this.terrain.id) {
        this.terrain.name = this.originalName;
        this.modalService.showMesssageModal(
          'Terrain Error',
          'Terrain name already exists. Please choose a diferent name.'
        );
        return;
      }
    }
    this.close();
    if (this.terrain.active
      || this.configurationController.validateTerrainEditOrDelete(this.terrain, this.selectedRow)) {

      this.terrain.terrainTypeId = this.selectedType.id;
      if (this.zipFiles != null) {
        this.uploadTerrain(true);
        return;
      }
      this.uiBlock.startBlock('Sending and saving terrain...');
      const formData = ConfigurationTerrain.mapToFormData(this.terrain);
      let savedTerrain: ConfigurationTerrain;
      let error: string = null;
      try {
        savedTerrain = await this.configurationController.updateTerrain(formData);
      } catch (e) {
        e.error ? error = e.error : false;
        this.uiBlock.stopBlock();
      }
      if (savedTerrain == null) {
        if (error == null) {
          error = 'Error updating terrain: ' + this.terrain.name;
        }
        this.modalService.showMesssageModal('Update Terrain Error', error);
      } else {
        this.configurationController.savedTerrains$.getValue()[this.selectedRow] = savedTerrain;
      }
      this.uiBlock.stopBlock();
    } else {
      this.modalService.showMesssageModal("Terrain Error", "There must be at least one active DTED1 terrain in current project.");
    }
  }

  async saveTerrain() {
    for (const terrain of this.configurationController.savedTerrains$.getValue()) {
      if (terrain.name == this.terrain.name) {
        this.terrain.name = "";
        this.modalService.showMesssageModal(
          'Terrain Error',
          'Terrain name already exists. Please choose a diferent name.'
        );
        return;
      }
    }
    this.close();
    this.uploadTerrain();
  };


  async uploadTerrain(isUpdate: boolean = false) {
    this.closePreviousModal();
    const chunkSizeMB: number = await this.configurationController.getTerrainChunkSize();
    this.close();
    const fileChunks: File[] = UtilsService.divideFile(this.zipFiles, chunkSizeMB);
    if (!isUpdate) {
      this.terrain.projectId = this.projectService.activeProject.id;
      this.terrain.position = this.configurationController.savedTerrains$.getValue().length + 1;
      this.terrain.terrainTypeId = this.selectedType.id;
    }
    this.terrain.numberOfRequests = fileChunks.length;
    this.terrain.terrainSize = this.zipFiles.size
    const formData = ConfigurationTerrain.mapToFormData(this.terrain);
    try {
      //first request
      formData.set("requestNumber", "0");
      await this.configurationController.saveTerrain(formData, isUpdate)
        .catch(error => {
          // Add the status code to the error object
          if (error instanceof HttpErrorResponse) {
            throw { ...error, status: error.status };
          } else {
            throw error;
          }
        });
      // subsequent requests
      for (let i = 0; i < fileChunks.length; i++) {
        formData.set("terrainFile", fileChunks[i], fileChunks[i].name);
        formData.set("requestNumber", i.toString());
        formData.set('chunkSize', fileChunks[i].size.toString());
        this.configurationController.saveTerrain(formData, isUpdate)
          .catch(error => {
            // Add the status code to the error object
            if (error instanceof HttpErrorResponse) {
              throw { ...error, status: error.status };
            } else {
              throw error;
            }
          });
      }
    } catch (error) {
      if (error && error.status == 504) {
        return;
      }
      const uploadTerrainProgressMapKey: string = this.progressService.getTerrainUploadOnProgress();
      if (uploadTerrainProgressMapKey != null) {
        this.progressService.progressMap.delete(uploadTerrainProgressMapKey);
      }
      if (error && error.status == 507) {
        this.modalService.showMesssageModal
          (
            'Save Terrain Error',
            'User directory limit would be exceeded'
          )
        return;
      }
      if (error && error.status == 409) {
        this.modalService.showMesssageModal
          (
            'Save Terrain Error',
            'A terrain is being uploaded, you cannot modidy terrains. If this is not the case, please refresh the page.'
          )
        return;
      }

      this.modalService.showMesssageModal
        (
          'Save Terrain Error',
          'Error saving terrain: ' + this.terrain.name + '. Please make sure you are importing the correct file.'
        );
      return;
    }
  }

}
